import { TradingEngineError } from "../../shared/api/api-error.js";
import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import {
  useAccounts,
  useConnectionAction,
  useConnections,
  useConnectTastytrade,
} from "./queries.js";
import { TastytradeConnectionForm } from "./tastytrade-connection-form.js";

function displayTime(value: string): string {
  if (value === "") return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "Not available" : date.toLocaleString();
}

export function ConnectionsSection() {
  const connections = useConnections();
  const tastytrade = connections.data?.find((connection) => connection.provider === "tastytrade");
  const accounts = useAccounts(tastytrade?.id);
  const connect = useConnectTastytrade();
  const connectionAction = useConnectionAction();

  if (connections.isPending) {
    return (
      <ResourceState
        kind="loading"
        title="Loading broker connection"
        description="TradingApp is checking Tastytrade authorization and account readiness."
      />
    );
  }
  if (connections.isError) {
    return (
      <ResourceState
        kind="error"
        title="Broker connection unavailable"
        description={
          connections.error instanceof TradingEngineError &&
          connections.error.correlationId !== null
            ? `TradingEngine could not load the connection. Reference ${connections.error.correlationId}.`
            : "TradingEngine could not load the connection. Try again."
        }
        action={<Button onClick={() => void connections.refetch()}>Try again</Button>}
      />
    );
  }
  if (tastytrade === undefined) {
    return (
      <section className="content-card section-stack" aria-labelledby="connect-tastytrade-title">
        <div>
          <p className="eyebrow">Execution provider</p>
          <h2 id="connect-tastytrade-title">Connect Tastytrade</h2>
          <p className="section-description">
            Authorize stock execution and discover every broker account available to this identity.
          </p>
        </div>
        <TastytradeConnectionForm
          busy={connect.isPending}
          onConnect={async (credentials) => {
            await connect.mutateAsync(credentials);
          }}
        />
      </section>
    );
  }

  const reconnectRequired = ["expired", "revoked", "unavailable"].includes(tastytrade.status);
  return (
    <section className="section-stack" aria-labelledby="tastytrade-title">
      <div className="content-card connection-heading">
        <div>
          <p className="eyebrow">Execution provider</p>
          <h2 id="tastytrade-title">Tastytrade</h2>
          <p className="section-description">Last checked {displayTime(tastytrade.updatedAt)}</p>
        </div>
        <span className={`status-pill status-pill--${tastytrade.status}`}>{tastytrade.status}</span>
        <div className="button-row">
          <Button
            tone="quiet"
            disabled={connectionAction.isPending}
            onClick={() =>
              connectionAction.mutate({ action: "refresh", connectionId: tastytrade.id })
            }
          >
            Refresh accounts
          </Button>
          <Button
            tone="quiet"
            disabled={connectionAction.isPending || tastytrade.status === "disabled"}
            onClick={() =>
              connectionAction.mutate({ action: "disable", connectionId: tastytrade.id })
            }
          >
            Disable
          </Button>
        </div>
      </div>

      {reconnectRequired ? (
        <div className="content-card section-stack">
          <div>
            <h3>Reconnect required</h3>
            <p className="section-description">
              Submit fresh OAuth credentials before these accounts can be used for live trading.
            </p>
          </div>
          <TastytradeConnectionForm
            reconnect
            busy={connect.isPending}
            onConnect={async (credentials) => {
              await connect.mutateAsync(credentials);
            }}
          />
        </div>
      ) : null}

      <div className="content-card section-stack">
        <div>
          <h3>Authorized accounts</h3>
          <p className="section-description">
            TradingEngine supplies stable identifiers and current stock-trading capabilities.
          </p>
        </div>
        {accounts.isPending ? (
          <ResourceState
            kind="loading"
            title="Discovering accounts"
            description="Waiting for normalized Tastytrade account details."
          />
        ) : accounts.isError ? (
          <ResourceState
            kind="degraded"
            title="Account discovery is unavailable"
            description="The connection remains visible. Refresh when Tastytrade is available."
          />
        ) : accounts.data.length === 0 ? (
          <ResourceState
            kind="empty"
            title="No accounts discovered"
            description="Reconnect or refresh Tastytrade to discover authorized accounts."
          />
        ) : (
          <div className="account-grid">
            {accounts.data.map((account) => (
              <article className="account-card" key={account.id}>
                <div>
                  <h4>{account.displayName}</h4>
                  <span className={`status-pill status-pill--${account.status}`}>
                    {account.status}
                  </span>
                </div>
                <dl>
                  <div>
                    <dt>Broker account</dt>
                    <dd>{account.providerAccountId}</dd>
                  </div>
                  <div>
                    <dt>Capabilities</dt>
                    <dd>
                      {account.capabilities.length === 0
                        ? "Pending discovery"
                        : account.capabilities.join(", ")}
                    </dd>
                  </div>
                  <div>
                    <dt>Last update</dt>
                    <dd>{displayTime(account.updatedAt)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
